import { useContext, useEffect, useRef, useState } from 'react'
import { FileContext } from '../contexts/FileContext'
import { UploadContext } from '../contexts/UploadContext'
import useProductSearchSelect from './useProductSearchSelect'
import useMetadataForm from './useMetadataForm'

export default function useBundles({ product, csrfToken }) {
  const searchSelect = useProductSearchSelect(csrfToken)
  const metadataForm = useMetadataForm()
  const { isPatching } = useContext(FileContext)
  const { uploadsInProgress } = useContext(UploadContext)

  const { bundleTitle, bundleDescription, bundlePrice } = metadataForm
  const { selectedSearchResults } = searchSelect
  const snapshotRef = useRef(null)
  const [hasMetadataChanges, setHasMetadataChanges] = useState(false)
  const initialProductIds = String(product?.collectionProducts || '')
    .split(',')
    .filter(Boolean)
    .sort()

  const buildSnapshot = (overrides = {}) => ({
    title: overrides.title ?? (bundleTitle || '').trim(),
    description: overrides.description ?? (bundleDescription || '').trim(),
    price: overrides.price ?? (bundlePrice == null ? '' : String(bundlePrice)),
    productIds: overrides.productIds ?? Object.keys(selectedSearchResults || {}).sort(),
  })

  const markClean = (overrides = {}) => {
    snapshotRef.current = buildSnapshot(overrides)
    setHasMetadataChanges(false)
  }

  useEffect(() => {
    const next = buildSnapshot()
    const waitingForInitialSelectionHydration = initialProductIds.length > 0
      && (next.productIds.length !== initialProductIds.length
        || initialProductIds.some((id, index) => id !== next.productIds[index]))

    if (!snapshotRef.current) {
      if (waitingForInitialSelectionHydration) return
      snapshotRef.current = next
      return
    }

    const prev = snapshotRef.current
    const dirty = prev.title !== next.title
      || prev.description !== next.description
      || prev.price !== next.price
      || prev.productIds.length !== next.productIds.length
      || prev.productIds.some((id, index) => id !== next.productIds[index])

    setHasMetadataChanges(dirty)
  }, [bundleTitle, bundleDescription, bundlePrice, selectedSearchResults, product?.collectionProducts])

  return {
    searchSelect,
    metadataForm,
    dirty: {
      isDirty: hasMetadataChanges || isPatching || uploadsInProgress,
      markClean,
    },
  }
}
